import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { hasTier } from '@/admin/access';
import api from '@/api';
import { isErrorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import { formatLongDate } from '~/utils/dateFormat';
import { formatString } from '~/utils/stringFormat';

type Targets = components['schemas']['StudentConnectTargets'];
type Campaign = NonNullable<Targets['campaign']>;
type WebTarget = Targets['web_targets'][number];

/**
 * Staff view of where a student is currently listed, with the three levers
 * legacy put under it: Pass on (per target), Re-Target (re-run the geo match)
 * and Release Student.
 *
 * The read is AdminCheck; pass-on and re-target are SupportCheck, so they are
 * drawn only from the support tier up. Release Student is the study-helper
 * route's own AdminCheck write.
 */
export function ConnectTargetsPanel({ studentId }: { studentId: number }) {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const isStaff = hasTier(me, 'FRONT_END_SUPPORT');
  const canWrite = hasTier(me, 'SUPPORT');
  const [notice, setNotice] = useState<{
    variant: 'success' | 'error';
    text: string;
  } | null>(null);

  const { data } = api.getStudentConnectTargets.useQuery(
    { path: { student_id: studentId } },
    { enabled: isStaff && studentId > 0 },
  );

  const refresh = async () => {
    await Promise.all([
      api.getStudentConnectTargets.invalidateQueries(),
      api.getStudentStudyHelper.invalidateQueries(),
      api.getStudentStudyHelperHistory.invalidateQueries(),
    ]);
  };

  const passOn = api.passOnStudentConnectTarget.useMutation();
  const retarget = api.retargetStudentConnectTargets.useMutation();
  const release = api.setStudentStudyHelper.useMutation();

  if (!isStaff || data === undefined) return null;

  const fail = (error: unknown) =>
    setNotice({
      variant: 'error',
      text: isErrorCode(error, 'student_location_unknown')
        ? t('wbs.user-has-no-geo-data')
        : t('wbs.something-went-wrong-please'),
    });

  const run = async (action: () => Promise<unknown>, success?: string) => {
    setNotice(null);
    try {
      await action();
      if (success) setNotice({ variant: 'success', text: success });
    } catch (error) {
      fail(error);
    }
    await refresh();
  };

  const status = (active: boolean) =>
    active ? t('wbs.active-2') : t('wbs.passed-on');
  const groupName = (group: { name: string } | null) =>
    group?.name ?? t('wbs.global');

  const campaignText = (campaign: Campaign) =>
    campaign.domain
      ? formatString(
          t('wbs.connect-target-campaign-item'),
          campaign.title,
          campaign.domain,
          groupName(campaign.group),
          status(campaign.active),
        )
      : formatString(
          t('wbs.connect-target-web-item'),
          campaign.title,
          groupName(campaign.group),
          status(campaign.active),
        );

  const webTargetText = (target: WebTarget) =>
    formatString(
      t('wbs.connect-target-web-item'),
      // A soft-deleted target has no title.
      target.title ??
        formatString(t('actions.deleted-web-target-s'), String(target.id)),
      groupName(target.group),
      status(target.active),
    );

  return (
    <Panel data-testid="connect-targets-panel">
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.student-connect-targets')}
          placement="study_helpers"
        />
      </GroupHeaderWrapper>

      {notice && <AlertBox variant={notice.variant}>{notice.text}</AlertBox>}

      <Row>
        <Label>{t('wbs.intro-course-completed')}</Label>
        <Note>{t('wbs.intro-course-completed-note')}</Note>
        {data.intro_course_completed ? t('wbs.yes') : t('wbs.no')}
      </Row>
      <Row>
        <Label>{t('wbs.request-to-be-listed-early')}</Label>
        <Note>{t('wbs.request-to-be-listed-early-note')}</Note>
        {data.listed_early ? t('wbs.yes') : t('wbs.no')}
      </Row>

      <Row>
        <Label>{t('wbs.matched-campaign-2')}</Label>
        <Note>{t('wbs.connect-targets-campaign-note')}</Note>
        {data.campaign ? (
          <Item>
            {campaignText(data.campaign)}
            {canWrite && data.campaign.active && (
              <WBSButton
                data-testid="pass-on-campaign"
                isLoading={passOn.isPending}
                onClick={() =>
                  run(() =>
                    passOn.mutateAsync({
                      path: { student_id: studentId },
                      body: { campaign_id: data.campaign?.id ?? null },
                    }),
                  )
                }
              >
                {t('wbs.pass-on-2')}
              </WBSButton>
            )}
          </Item>
        ) : (
          <Item>{t('wbs.n-a-2')}</Item>
        )}
      </Row>

      <Row>
        <Label>{t('wbs.matched-web-targets')}</Label>
        <Note>{t('wbs.connect-targets-web-target-note')}</Note>
        {data.web_targets.length === 0 ? (
          <Item>{t('wbs.not-matched-with-any-web-targets')}</Item>
        ) : (
          data.web_targets.map((target) => (
            <Item key={target.id}>
              {webTargetText(target)}
              {canWrite && target.active && (
                <WBSButton
                  data-testid={`pass-on-web-target-${target.id}`}
                  isLoading={passOn.isPending}
                  onClick={() =>
                    run(() =>
                      passOn.mutateAsync({
                        path: { student_id: studentId },
                        body: { web_target_id: target.id },
                      }),
                    )
                  }
                >
                  {t('wbs.pass-on-2')}
                </WBSButton>
              )}
            </Item>
          ))
        )}
      </Row>

      <Row>
        <Label>{t('wbs.waiting-on-study-helper-request')}</Label>
        <Note>{t('wbs.connect-targets-waiting-note')}</Note>
        {data.adoption_requests.length === 0 ? (
          <Item>{t('wbs.not-waiting-for-any-study-helper-request')}</Item>
        ) : (
          data.adoption_requests.map((request) => (
            <Item key={request.id}>
              {formatString(
                t('wbs.connect-target-request-item'),
                request.study_helper.display_name,
                formatLongDate(request.created_at),
                request.status,
              )}
            </Item>
          ))
        )}
      </Row>

      {canWrite && (
        <Row>
          <Note>{t('wbs.force-regenerate-geo-matching')}</Note>
          <WBSButton
            data-testid="retarget"
            isLoading={retarget.isPending}
            onClick={() =>
              run(() =>
                retarget.mutateAsync({ path: { student_id: studentId } }),
              )
            }
          >
            {t('wbs.re-target')}
          </WBSButton>
        </Row>
      )}

      {data.study_helper_id !== null && (
        <Row>
          <WBSButton
            data-testid="release-student"
            isLoading={release.isPending}
            onClick={() => {
              if (!window.confirm(t('wbs.are-you-sure-this'))) return;
              void run(
                () =>
                  release.mutateAsync({
                    path: { student_id: studentId },
                    body: { study_helper_id: null },
                  }),
                t('wbs.student-released'),
              );
            }}
          >
            {t('wbs.release-student')}
          </WBSButton>
        </Row>
      )}
    </Panel>
  );
}

const Panel = styled.section`
  margin-top: 40px;
`;

const Row = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.strong`
  font-weight: 500;
`;

const Note = styled.p`
  color: var(--grey-700);
  font-size: 12px;
  margin: 4px 0 8px;
`;

const Item = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 6px;
`;
